import {
  DefaultSpinner,
  DurationViewer,
  FixedScreenHeaderStack,
  Heading,
  SearchAndAddBar,
} from '~/common/components/defaultUIComponents';
import SpecialJobApi from '~/common/api/zeiterfassungServerApi/SpecialJobApi';
import { SpecialJob } from '~/common/entities/SpecialJobs/SpecialJobsTypes';
import EmployeeApi from '~/common/api/zeiterfassungServerApi/EmployeeApi';
import { Employee } from '~/common/entities/Department/DepartmentTypes';
import { Divider, Grid, GridItem, Text } from '@chakra-ui/react';
import { SpecialJobContextProvider } from './specialJobContext';
import { formatName } from '~/common/utils/employeeUtils';
import { DatePickerInput } from 'chakra-datetime-picker';
import { ListView } from '~/common/components/ListView';
import routes from '~/common/constants/routes.json';
import useTranslate from '~/common/utils/translate';
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SpecialJobTile from './SpecialJobTile';
import * as dateFns from 'date-fns';
import _ from 'lodash';

type EmployeeSpecialJobTuple = [Employee, SpecialJob[], number];

const isoDateFormat = 'yyyy-MM-dd';

function getStartOfPreviousMonth() {
  return dateFns.format(
    dateFns.startOfMonth(dateFns.subMonths(new Date(), 1)),
    isoDateFormat,
  );
}

function getTomorrow() {
  return dateFns.format(dateFns.addDays(new Date(), 1), isoDateFormat);
}

export const SpecialJobs: React.FC = () => {
  const translate = useTranslate();
  const [searchTerm, setSearchTerm] = useState('');
  const [beginningDate, setBeinningDate] = useState(getStartOfPreviousMonth());
  const [endDate, setEndDate] = useState(getTomorrow());
  const [openEmployee, setOpenEmployee] = useState<number | null | undefined>();
  const navigate = useNavigate();

  const { data: specialJobs = [], isLoading: specialJobsLoading } =
    SpecialJobApi.hooks.useSpecialJobsQuery({
      start: beginningDate,
      end: endDate,
    });
  const [{ data: employees = [], isLoading: employeesLoading }] =
    EmployeeApi.hooks.useEmployeeQuery();

  const employeesAndSJ = useSpecialJobsByEmployee(
    specialJobs,
    employees,
    beginningDate,
    endDate,
  );

  return (
    <SpecialJobContextProvider
      value={{
        openEmployee,
        setOpenEmployee,
      }}
    >
      <FixedScreenHeaderStack>
        <Heading>specialJobs.heading</Heading>
        <SearchAndAddBar
          onInputChange={setSearchTerm}
          onClick={() => navigate(routes.CREATE_SPECIAL_JOB)}
          inputPlaceholder="specialJobs.searchEmployees.input.placeholder"
          tooltipLabel="specialJobs.addSpecialJob.tooltip.label"
        />
      </FixedScreenHeaderStack>
      <Grid mt={5} templateColumns="repeat(2, 1fr)">
        <GridItem colSpan={2}>
          <Text as="b" color="black" fontSize="2xl">
            {translate('specialJobs.filter.header')}
          </Text>
        </GridItem>
        <GridItem colSpan={1}>
          <DatePickerInput
            value={beginningDate}
            onChange={(selected: string) => setBeinningDate(selected)}
            clearText={translate('specialJobs.filter.reset')}
            placeholder={translate('specialJobs.filter.begin')}
          />
        </GridItem>
        <GridItem colSpan={1}>
          <DatePickerInput
            value={endDate}
            onChange={(selected: string) => setEndDate(selected)}
            clearText={translate('specialJobs.filter.reset')}
            placeholder={translate('specialJobs.filter.end')}
          />
        </GridItem>
      </Grid>
      {employeesAndSJ && (
        <Grid mt={5} templateColumns="repeat(2, 1fr)">
          {/* eslint-disable-next-line @typescript-eslint/no-unused-vars */}
          {employeesAndSJ.map(([employee, _, duration]) => (
            <React.Fragment key={employee.id}>
              <GridItem colSpan={1}>
                <Text>
                  {employee.user.first_name + ' ' + employee.user.last_name}
                </Text>
              </GridItem>
              <GridItem colSpan={1}>
                <DurationViewer
                  durationInSeconds={duration}
                  label="specialJobs.filter.totalTime"
                />
              </GridItem>
            </React.Fragment>
          ))}
        </Grid>
      )}
      {!employeesAndSJ && (employeesLoading || specialJobsLoading) && (
        <DefaultSpinner h="unset" mt={5} />
      )}
      <ListView
        data={employeesAndSJ}
        searchFields={(e) => [
          e[0].user.first_name,
          e[0].user.last_name,
          formatName(e[0]),
        ]}
        search={searchTerm}
        mt={5}
        render={({ data }) => {
          return (
            <>
              {data.map(([employee, specialJobs]) => (
                <React.Fragment key={employee.id}>
                  <SpecialJobTile
                    key={employee.id}
                    employee={employee}
                    specialJobs={specialJobs}
                  />
                  <Divider />
                </React.Fragment>
              ))}
            </>
          );
        }}
      />
    </SpecialJobContextProvider>
  );
};

function useSpecialJobsByEmployee(
  specialJobs: SpecialJob[],
  employees: Employee[],
  beginningDate: string,
  endDate: string,
) {
  return useMemo(() => {
    if (!specialJobs || !employees) {
      return [];
    }

    return _.orderBy(
      employees.map((e) => {
        const specialJobsOfEmployee = specialJobs.filter(
          (sj) => sj.employee == e.id,
        );

        const duration = specialJobsOfEmployee.reduce(
          (total, sj) => total + (sj.special_job_duration_in_timeframe ?? 0),
          0,
        );
        return [e, specialJobsOfEmployee, duration] as EmployeeSpecialJobTuple;
      }),
      (e) => formatName(e[0]),
    );
  }, [specialJobs, employees, beginningDate, endDate]);
}
